/**
 * PRP-INTAKE-1 (I4): the estimate's open items, answered right on the page.
 *
 * Reads GET /mcp/estimates/{id}/open-items: one entry per request (grouped
 * across rooms), its candidates, the recommended one and why. Each item is
 * answered with radio buttons and Answer; with several rooms the answer goes
 * to every room unless "Apply to all N rooms" is unticked, which offers a
 * choice per room (each room is its own proposal). "Accept all recommended"
 * answers every item that has a recommendation. Answers use the same server
 * path the chat uses. "Review in chat" copies a one-line prompt that resumes
 * the interview in the user's chat. Renders nothing while no item is open.
 */
import { useCallback, useEffect, useState } from 'react';

import { McpAPI } from '../../lib/mcpApi';
import type { McpOpenItem, McpOpenItems, McpOpenItemsAnswer } from '../../lib/mcpApi';
import { Banner } from './ui/banner';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Pill } from './ui/pill';

/** Props for {@link OpenItemsBanner}. */
export interface OpenItemsBannerProps {
  estimateId: string;
  /** Re-fetch the estimate after an answer changed it. */
  onChanged?: () => void | Promise<void>;
  /** Refetch when this changes (the estimate's payload version). */
  refreshKey?: string;
}

function messageOf(err: unknown): string {
  const m = (err as { message?: string } | undefined)?.message;
  return m && m.trim() ? m : 'That did not go through. Please try again.';
}

export default function OpenItemsBanner({ estimateId, onChanged, refreshKey }: OpenItemsBannerProps) {
  const [data, setData] = useState<McpOpenItems | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rejected, setRejected] = useState<Record<string, string>>({});
  const [copied, setCopied] = useState<'yes' | 'no' | null>(null);

  const load = useCallback(async () => {
    try {
      // An older server or an unexpected body must not break the estimate page: normalise once here.
      const got = await McpAPI.getOpenItems(estimateId);
      setData(got && Array.isArray(got.items)
        ? { ...got, items: got.items.map((i) => ({ ...i, rooms: i.rooms ?? [], options: i.options ?? [] })),
            assumptions: (got.assumptions ?? []).map((a) => ({ ...a, rooms: a.rooms ?? [] })) }
        : null);
    } catch {
      setData(null); // 404 (no chat history, older server) or a failure: show nothing.
    }
  }, [estimateId]);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  const run = async (call: () => Promise<McpOpenItemsAnswer>) => {
    setBusy(true);
    setError(null);
    try {
      const res = await call();
      const next: Record<string, string> = {};
      for (const r of res.results ?? []) {
        if (r.status === 'rejected') next[r.proposal_id] = r.reason || 'Not applied.';
      }
      setRejected(next);
      await load();
      await onChanged?.();
    } catch (err) {
      setError(messageOf(err));
    } finally {
      setBusy(false);
    }
  };

  const copyPrompt = async () => {
    if (!data) return;
    try {
      await navigator.clipboard.writeText(data.review_prompt);
      setCopied('yes');
    } catch {
      setCopied('no');
    }
  };

  if (!data || data.items.length === 0) return null;
  const canAcceptAll = data.items.some((i) => i.answerable && i.recommended !== null);

  return (
    <Card className="px-4 py-3 mb-3" data-testid="open-items" aria-label="Open items">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[13px] font-semibold text-[var(--tx-hi)]">
          Open items ({data.items.length})
        </span>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {copied === 'yes' && (
            <span role="status" className="text-[12px] text-[var(--ok)]">Copied</span>
          )}
          {copied === 'no' && (
            <span role="status" className="text-[12px] text-[var(--tx-lo)]">
              Copy failed. The prompt: {data.review_prompt}
            </span>
          )}
          <Button size="sm" onClick={() => void copyPrompt()}>
            Review in chat
          </Button>
          <Button
            size="sm"
            variant="primary"
            disabled={busy || !canAcceptAll}
            onClick={() => void run(() => McpAPI.acceptRecommended(estimateId))}
          >
            Accept all recommended
          </Button>
        </div>
      </div>
      {error && (
        <Banner tone="warn" className="mt-2">
          {error}
        </Banner>
      )}
      <ul className="m-0 mt-2 p-0 list-none divide-y divide-[var(--bd-subtle)]">
        {data.items.map((item) => (
          <OpenItemRow
            key={item.key}
            item={item}
            busy={busy}
            rejected={rejected}
            onAnswer={(proposal_id, choice, same_request) =>
              void run(() =>
                McpAPI.answerOpenItem(estimateId, { proposal_id, choice, same_request }),
              )
            }
          />
        ))}
      </ul>
      {data.assumptions.length > 0 && (
        <details className="mt-2 text-[12px] text-[var(--tx-mid)]">
          <summary className="cursor-pointer">Settled by default ({data.assumptions.length})</summary>
          <ul className="m-0 mt-1 pl-5 list-disc">
            {data.assumptions.map((a, i) => (
              <li key={`${a.request}-${i}`}>
                {a.request} &rarr; {a.choice} ({a.default})
                {a.rooms.length > 0 && ` · ${a.rooms.join(', ')}`}
              </li>
            ))}
          </ul>
        </details>
      )}
    </Card>
  );
}

interface OpenItemRowProps {
  item: McpOpenItem;
  busy: boolean;
  rejected: Record<string, string>;
  onAnswer: (_proposalId: string, _choice: number, _sameRequest: boolean) => void;
}

function OpenItemRow({ item, busy, rejected, onAnswer }: OpenItemRowProps) {
  const [choice, setChoice] = useState<number | null>(item.recommended);
  const [applyAll, setApplyAll] = useState(true);
  const [perRoom, setPerRoom] = useState<Record<string, number | null>>({});
  const roomNames = item.rooms.map((r) => r.room).join(', ');
  const reasons = item.rooms.map((r) => rejected[r.proposal_id]).filter(Boolean);
  const many = item.rooms.length > 1;

  return (
    <li className="py-2.5" data-testid={`open-item-${item.key}`}>
      <div className="flex flex-wrap items-baseline gap-2">
        <span className="text-[12.5px] font-[550] text-[var(--tx-hi)]">{item.question}</span>
        <Pill kind="warn">{item.status}</Pill>
      </div>
      <div className="text-[11.5px] text-[var(--tx-lo)]">
        {item.request}
        {roomNames && ` · ${roomNames}`}
      </div>
      {item.answerable && (
        <div className="mt-1.5 space-y-1.5">
          {many && (
            <label className="flex items-center gap-1.5 text-[12px] text-[var(--tx-mid)]">
              <input
                type="checkbox"
                checked={applyAll}
                onChange={(e) => setApplyAll(e.target.checked)}
                className="accent-[var(--accent)]"
              />
              Apply to all {item.rooms.length} rooms
            </label>
          )}
          {applyAll || !many ? (
            <ChoiceGroup
              name={`oi-${item.key}`}
              item={item}
              value={choice}
              onChange={setChoice}
              busy={busy}
              label={item.question}
              onAnswer={() => choice !== null && onAnswer(item.rooms[0].proposal_id, choice, true)}
            />
          ) : (
            item.rooms.map((r) => {
              const v = r.proposal_id in perRoom ? perRoom[r.proposal_id] : item.recommended;
              return (
                <div key={r.proposal_id} className="pl-3 border-l border-[var(--bd-subtle)]">
                  <div className="text-[12px] font-[550] text-[var(--tx-mid)]">{r.room}</div>
                  <ChoiceGroup
                    name={`oi-${item.key}-${r.proposal_id}`}
                    item={item}
                    value={v}
                    onChange={(c) => setPerRoom((p) => ({ ...p, [r.proposal_id]: c }))}
                    busy={busy}
                    label={`${item.question} (${r.room})`}
                    onAnswer={() => v !== null && onAnswer(r.proposal_id, v, false)}
                  />
                </div>
              );
            })
          )}
        </div>
      )}
      {reasons.map((r, i) => (
        <p key={i} className="m-0 mt-1 text-[12px] text-[var(--bad)]">
          {r}
        </p>
      ))}
    </li>
  );
}

interface ChoiceGroupProps {
  name: string;
  item: McpOpenItem;
  value: number | null;
  onChange: (_c: number) => void;
  busy: boolean;
  label: string;
  onAnswer: () => void;
}

function ChoiceGroup({ name, item, value, onChange, busy, label, onAnswer }: ChoiceGroupProps) {
  return (
    <div className="space-y-1">
      <div role="radiogroup" aria-label={label} className="space-y-0.5">
        {item.options.map((o) => (
          <label
            key={o.choice}
            className="flex items-start gap-1.5 text-[12.5px] text-[var(--tx-hi)] cursor-pointer"
          >
            <input
              type="radio"
              name={name}
              value={o.choice}
              checked={value === o.choice}
              onChange={() => onChange(o.choice)}
              className="mt-[3px] accent-[var(--accent)]"
            />
            <span>
              {o.code && <span className="font-[var(--font-mono)] font-[550]">{o.code} </span>}
              {o.label}
              {item.recommended === o.choice && (
                <>
                  {' '}
                  <Pill kind="ok">Recommended</Pill>
                  {item.reason && (
                    <span className="block text-[11.5px] text-[var(--tx-lo)]">{item.reason}</span>
                  )}
                </>
              )}
            </span>
          </label>
        ))}
      </div>
      <Button
        size="sm"
        disabled={busy || value === null}
        onClick={onAnswer}
        aria-label={`Answer: ${label}`}
      >
        Answer
      </Button>
    </div>
  );
}
